import React, { useState, useEffect, useMemo } from 'react'
import ProfileCard from '../common/profileCard'
import StudentSidebar from '../common/studentSidebar'
import { Link, useLocation } from 'react-router-dom'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import { all_routes } from '../../router/all_routes'
import { getUserDashboard, UserDashboardResponse } from '../../../api/user'
import { Course, getCourses } from '../../../api/courses'

const StudentDashboard = () => {
  const [
    dashboardData,
    setDashboardData,
  ] = useState<UserDashboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [publicLiveTeaching, setPublicLiveTeaching] = useState<Course[]>([])
  const [calendarMonth, setCalendarMonth] = useState(() => new Date())
  const location = useLocation()
  const activeDashboardTab = new URLSearchParams(location.search).get('tab') === 'event'
    ? 'event'
    : 'dashboard'

  useEffect(() => {
    const getAllLiveTeachingEvents = async () => {
      const firstPage = await getCourses({
        type: 'Live_Teaching',
        page: 1,
        limit: 100,
      })
      let allEvents = firstPage?.data || []
      const lastPage = firstPage?.last_page || 1

      for (let page = 2; page <= lastPage; page += 1) {
        const nextPage = await getCourses({
          type: 'Live_Teaching',
          page,
          limit: 100,
        })
        allEvents = [...allEvents, ...(nextPage?.data || [])]
      }

      return allEvents
    }

    // Fetch dashboard data
    const fetchDashboard = async () => {
      try {
        const data = await getUserDashboard()
        let liveTeachingEvents: Course[] = []

        try {
          liveTeachingEvents = await getAllLiveTeachingEvents()
        } catch (eventError) {
          console.warn('Gagal memuat data Live Teaching publik:', eventError)
        }

        setDashboardData(data)
        setPublicLiveTeaching(liveTeachingEvents)

        const datedLiveTeaching = [
          ...liveTeachingEvents,
          ...(data?.live_teaching || []),
        ]
          .filter((event: any) => event?.start_date)
          .sort(
            (a: any, b: any) =>
              new Date(a.start_date).getTime() -
              new Date(b.start_date).getTime(),
          )
        const nextEvent =
          datedLiveTeaching.find(
            (event: any) => new Date(event.start_date).getTime() >= Date.now(),
          ) || datedLiveTeaching[0]

        if (nextEvent?.start_date) {
          const eventDate = new Date(nextEvent.start_date)
          setCalendarMonth(
            new Date(eventDate.getFullYear(), eventDate.getMonth(), 1),
          )
        }
        setLoading(false)
        // For development: log the data structure
        console.log('Dashboard data:', data)
      } catch (err) {
        setError('Gagal memuat data dashboard')
        setLoading(false)
        console.error(err)
      }
    }

    fetchDashboard()
  }, [])

  // Helper function to check if live teaching is upcoming (based on start_date)
  const isUpcomingLiveTeaching = (liveTeaching: any) => {
    if (!liveTeaching?.start_date) return false
    const start = new Date(liveTeaching.start_date)
    return start.getTime() >= Date.now()
  }

  // Get upcoming live teaching sessions
  const upcomingLiveTeaching = (
    dashboardData?.live_teaching?.filter(isUpcomingLiveTeaching) || []
  )
    .sort(
      (a: any, b: any) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
    )
    .slice(0, 1)

  const nextLiveTeaching = upcomingLiveTeaching[0] || null

  const [remainingMs, setRemainingMs] = useState<number | null>(null)

  useEffect(() => {
    if (!nextLiveTeaching?.start_date) {
      setRemainingMs(null)
      return
    }

    const startTime = new Date(nextLiveTeaching.start_date).getTime()

    const tick = () => {
      const diff = startTime - Date.now()
      setRemainingMs(diff > 0 ? diff : 0)
    }

    tick()
    const intervalId = setInterval(tick, 1000)
    return () => clearInterval(intervalId)
  }, [nextLiveTeaching?.start_date])

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const pad = (n: number) => n.toString().padStart(2, '0')

    const hhmmss = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    return days > 0 ? `${days} hari ${hhmmss}` : hhmmss
  }

  const liveTeachingEvents = useMemo(
    () => {
      const ownedIds = new Set(
        (dashboardData?.live_teaching || []).map((event: any) =>
          Number(event.id),
        ),
      )
      const eventMap = new Map<number, any>()

      publicLiveTeaching.forEach((event) => {
        eventMap.set(Number(event.id), {
          ...event,
          is_bought: ownedIds.has(Number(event.id)),
        })
      })

      ;(dashboardData?.live_teaching || []).forEach((event: any) => {
        eventMap.set(Number(event.id), {
          ...eventMap.get(Number(event.id)),
          ...event,
          is_bought: true,
        })
      })

      return Array.from(eventMap.values())
        .filter((event: any) => event?.start_date)
        .sort(
          (a: any, b: any) =>
            new Date(a.start_date).getTime() -
            new Date(b.start_date).getTime(),
        )
    },
    [dashboardData?.live_teaching, publicLiveTeaching],
  )

  const eventDateKey = (dateInput: string | Date) => {
    const date = new Date(dateInput)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const eventsByDate = useMemo(() => {
    return liveTeachingEvents.reduce((acc: Record<string, any[]>, event: any) => {
      const key = eventDateKey(event.start_date)
      acc[key] = [...(acc[key] || []), event]
      return acc
    }, {})
  }, [liveTeachingEvents])

  const monthLabel = calendarMonth.toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  })

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear()
    const month = calendarMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const mondayOffset = (firstDay.getDay() + 6) % 7
    const days = []

    for (let index = 0; index < mondayOffset; index += 1) {
      days.push({
        date: new Date(year, month, index - mondayOffset + 1),
        isCurrentMonth: false,
      })
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true,
      })
    }

    while (days.length % 7 !== 0) {
      const nextDay = days.length - mondayOffset - lastDay.getDate() + 1
      days.push({
        date: new Date(year, month + 1, nextDay),
        isCurrentMonth: false,
      })
    }

    return days
  }, [calendarMonth])

  const goToCurrentMonth = () => setCalendarMonth(new Date())

  const changeCalendarMonth = (step: number) => {
    setCalendarMonth(
      (current) => new Date(current.getFullYear(), current.getMonth() + step, 1),
    )
  }

  const formatEventTime = (dateInput: string) =>
    new Date(dateInput).toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

  const getLiveTeachingRoute = (event: any) => {
    if (!event?.id) return all_routes.liveTeaching
    return event?.is_bought
      ? all_routes.studentLiveEventDetails.replace(':id', String(event.id))
      : all_routes.liveTeachingDetail.replace(':id', String(event.id))
  }

  return (
    <>
      <div className="content">
        <div className="container">
          {/* profile box */}
          <ProfileCard />
          {/* profile box */}
          <div className="row">
            {/* sidebar */}
            <StudentSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              {loading ? (
                <div className="text-center my-5">Loading...</div>
              ) : error ? (
                <div className="alert alert-danger my-5">{error}</div>
              ) : (
                <>
                  {activeDashboardTab === 'dashboard' && (
                    <>
                  <div className="row">
                    <div className="col-md-6 col-xl-4">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <span className="icon-box bg-primary-transparent me-2 me-xxl-3 flex-shrink-0">
                              <ImageWithBasePath
                                src="/assets/img/icon/graduation.svg"
                                alt=""
                              />
                            </span>
                            <div>
                              <span className="d-block">Live Teaching</span>
                              <h4 className="fs-24 mt-1">
                                {dashboardData?.live_teaching_count ?? 0}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-xl-4">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <span className="icon-box bg-secondary-transparent me-2 me-xxl-3 flex-shrink-0">
                              <ImageWithBasePath
                                src="/assets/img/icon/book.svg"
                                alt=""
                              />
                            </span>
                            <div>
                              <span className="d-block">Jadi Course</span>
                              <h4 className="fs-24 mt-1">
                                {dashboardData?.course_count ?? 0}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-xl-4">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex align-items-center">
                            <span className="icon-box bg-success-transparent me-2 me-xxl-3 flex-shrink-0">
                              <ImageWithBasePath
                                src="/assets/img/icon/bookmark.svg"
                                alt=""
                              />
                            </span>
                            <div>
                              <span className="d-block">Jadi UKOM</span>
                              <h4 className="fs-24 mt-1">
                                {dashboardData?.cbt_count ?? 0}
                              </h4>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Countdown Card for Upcoming Live Teaching */}
                  {upcomingLiveTeaching.length > 0 && (
                    <div className="card bg-light quiz-ans-card mb-4">
                      <ImageWithBasePath
                        src="/assets/img/shapes/withdraw-bg1.svg"
                        className="quiz-ans-bg1"
                        alt="img"
                      />
                      <ImageWithBasePath
                        src="/assets/img/shapes/withdraw-bg2.svg"
                        className="quiz-ans-bg2"
                        alt="img"
                      />
                      <div className="card-body">
                        {/* Countdown Timer */}
                        <div className="mb-3">
                          <span className="badge bg-warning text-dark fs-14">
                            {remainingMs !== null
                              ? `Live Teaching mulai dalam: ${formatDuration(
                                remainingMs,
                              )}`
                              : 'Jadwal Live Teaching belum tersedia'}
                          </span>
                        </div>
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div>
                              <h6 className="mb-1">
                                {nextLiveTeaching?.title ||
                                  'Live Teaching Session'}
                              </h6>
                              <p>
                                {nextLiveTeaching?.start_date
                                  ? new Date(
                                    nextLiveTeaching.start_date,
                                  ).toLocaleString('id-ID', {
                                    weekday: 'long',
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                  }) + ' WIB'
                                  : '-'}
                              </p>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-end">
                              <Link
                                to={
                                  nextLiveTeaching?.id
                                    ? all_routes.studentLiveEventDetails.replace(
                                      ':id',
                                      String(nextLiveTeaching.id),
                                    )
                                    : '#'
                                }
                                className="btn btn-primary rounded-pill"
                              >
                                Gabung Kelas
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                    </>
                  )}

                  {activeDashboardTab === 'event' && (
                  <section className="student-event-calendar mb-4">
                    <style>
                      {`
                        .student-event-calendar {
                          background: #0f1b2d;
                          border-radius: 8px;
                          color: #eef7ff;
                          overflow: hidden;
                          box-shadow: 0 12px 28px rgba(15, 27, 45, 0.16);
                        }
                        .student-event-calendar .calendar-header {
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 12px;
                          padding: 18px 18px 14px;
                          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                        }
                        .student-event-calendar .calendar-title span {
                          color: #9fb3c8;
                          display: block;
                          font-size: 12px;
                          line-height: 1.3;
                        }
                        .student-event-calendar .calendar-title h5 {
                          color: #fff;
                          margin: 2px 0 0;
                          text-transform: capitalize;
                        }
                        .student-event-calendar .calendar-actions {
                          display: flex;
                          align-items: center;
                          gap: 8px;
                          flex-wrap: wrap;
                          justify-content: flex-end;
                        }
                        .student-event-calendar .calendar-action-btn,
                        .student-event-calendar .calendar-nav-btn {
                          border: 1px solid rgba(255, 255, 255, 0.12);
                          background: rgba(255, 255, 255, 0.08);
                          color: #fff;
                          height: 34px;
                          display: inline-flex;
                          align-items: center;
                          justify-content: center;
                          border-radius: 6px;
                          font-size: 13px;
                          transition: all 0.2s ease;
                        }
                        .student-event-calendar .calendar-action-btn {
                          padding: 0 12px;
                        }
                        .student-event-calendar .calendar-nav-btn {
                          width: 34px;
                        }
                        .student-event-calendar .calendar-action-btn:hover,
                        .student-event-calendar .calendar-nav-btn:hover {
                          background: #00b8d9;
                          border-color: #00b8d9;
                          color: #fff;
                        }
                        .student-event-calendar .calendar-weekdays,
                        .student-event-calendar .calendar-grid {
                          display: grid;
                          grid-template-columns: repeat(7, minmax(0, 1fr));
                        }
                        .student-event-calendar .calendar-weekdays {
                          padding: 14px 14px 0;
                          gap: 8px;
                        }
                        .student-event-calendar .calendar-weekday {
                          color: #b9c8d8;
                          font-size: 11px;
                          font-weight: 700;
                          text-align: center;
                          text-transform: uppercase;
                        }
                        .student-event-calendar .calendar-grid {
                          gap: 8px;
                          padding: 12px 14px 16px;
                        }
                        .student-event-calendar .calendar-day {
                          min-height: 118px;
                          padding: 10px;
                          border: 1px solid rgba(255, 255, 255, 0.08);
                          border-radius: 8px;
                          background: rgba(255, 255, 255, 0.04);
                          color: inherit;
                          text-align: left;
                        }
                        .student-event-calendar .calendar-day.is-muted {
                          opacity: 0.38;
                        }
                        .student-event-calendar .calendar-day.is-today {
                          border-color: #00b8d9;
                          box-shadow: inset 0 0 0 1px #00b8d9;
                        }
                        .student-event-calendar .calendar-date {
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          color: #dbeafe;
                          font-size: 18px;
                          font-weight: 700;
                          margin-bottom: 8px;
                        }
                        .student-event-calendar .today-pill {
                          background: #00b8d9;
                          border-radius: 999px;
                          color: #fff;
                          font-size: 10px;
                          padding: 3px 7px;
                        }
                        .student-event-calendar .calendar-event {
                          display: block;
                          background: linear-gradient(135deg, #fff8df 0%, #f6c450 100%);
                          border: 1px solid rgba(255, 255, 255, 0.45);
                          border-radius: 8px;
                          color: #24364d;
                          font-size: 11px;
                          font-weight: 700;
                          line-height: 1.25;
                          margin-top: 6px;
                          padding: 8px;
                          min-height: 54px;
                        }
                        .student-event-calendar .calendar-event:hover {
                          color: #132235;
                          transform: translateY(-1px);
                        }
                        .student-event-calendar .calendar-event-time {
                          align-items: center;
                          color: #46617f;
                          display: flex;
                          gap: 4px;
                          font-size: 10px;
                          font-weight: 600;
                          margin-bottom: 4px;
                        }
                        .student-event-calendar .calendar-event-title {
                          display: -webkit-box;
                          -webkit-line-clamp: 2;
                          -webkit-box-orient: vertical;
                          overflow: hidden;
                        }
                        .student-event-calendar .calendar-empty {
                          color: #8da4bd;
                          font-size: 12px;
                          padding-top: 8px;
                        }
                        .student-event-calendar .calendar-footer {
                          display: flex;
                          align-items: center;
                          justify-content: space-between;
                          gap: 12px;
                          padding: 0 18px 18px;
                        }
                        .student-event-calendar .calendar-footer small {
                          color: #9fb3c8;
                        }
                        @media (max-width: 767px) {
                          .student-event-calendar .calendar-header,
                          .student-event-calendar .calendar-footer {
                            align-items: flex-start;
                            flex-direction: column;
                          }
                          .student-event-calendar .calendar-weekdays {
                            display: none;
                          }
                          .student-event-calendar .calendar-grid {
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                          }
                          .student-event-calendar .calendar-day {
                            min-height: 104px;
                          }
                        }
                        @media (max-width: 420px) {
                          .student-event-calendar .calendar-grid {
                            grid-template-columns: 1fr;
                          }
                        }
                      `}
                    </style>
                    <div className="calendar-header">
                      <div className="calendar-title">
                        <span>
                          {liveTeachingEvents.length} Live Teaching tersedia
                        </span>
                        <h5>{monthLabel}</h5>
                      </div>
                      <div className="calendar-actions">
                        <Link
                          to={all_routes.liveTeaching}
                          className="calendar-action-btn"
                        >
                          <i className="isax isax-calendar-1 me-1" />
                          Semua Event
                        </Link>
                        <button
                          type="button"
                          className="calendar-action-btn"
                          onClick={goToCurrentMonth}
                        >
                          Bulan ini
                        </button>
                        <button
                          type="button"
                          className="calendar-nav-btn"
                          onClick={() => changeCalendarMonth(-1)}
                          aria-label="Bulan sebelumnya"
                        >
                          <i className="isax isax-arrow-left-2" />
                        </button>
                        <button
                          type="button"
                          className="calendar-nav-btn"
                          onClick={() => changeCalendarMonth(1)}
                          aria-label="Bulan berikutnya"
                        >
                          <i className="isax isax-arrow-right-3" />
                        </button>
                      </div>
                    </div>
                    <div className="calendar-weekdays">
                      {['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'].map(
                        (dayName) => (
                          <div className="calendar-weekday" key={dayName}>
                            {dayName}
                          </div>
                        ),
                      )}
                    </div>
                    <div className="calendar-grid">
                      {calendarDays.map(({ date, isCurrentMonth }) => {
                        const key = eventDateKey(date)
                        const dayEvents = eventsByDate[key] || []
                        const isToday = key === eventDateKey(new Date())

                        return (
                          <div
                            className={`calendar-day${
                              isCurrentMonth ? '' : ' is-muted'
                            }${isToday ? ' is-today' : ''}`}
                            key={key}
                          >
                            <div className="calendar-date">
                              <span>{date.getDate()}</span>
                              {isToday && <span className="today-pill">Hari ini</span>}
                            </div>
                            {dayEvents.length > 0 ? (
                              dayEvents.slice(0, 2).map((event: any) => (
                                <Link
                                  key={event.id}
                                  to={getLiveTeachingRoute(event)}
                                  className="calendar-event"
                                  title={event.title}
                                >
                                  <span className="calendar-event-time">
                                    <i className="isax isax-clock" />
                                    {formatEventTime(event.start_date)} WIB
                                  </span>
                                  <span className="calendar-event-title">
                                    {event.title || 'Live Teaching'}
                                  </span>
                                </Link>
                              ))
                            ) : (
                              isCurrentMonth && (
                                <div className="calendar-empty">
                                  Tidak ada agenda
                                </div>
                              )
                            )}
                            {dayEvents.length > 2 && (
                              <Link
                                to={getLiveTeachingRoute(dayEvents[2])}
                                className="calendar-event"
                              >
                                +{dayEvents.length - 2} event lain
                              </Link>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    <div className="calendar-footer">
                      <small>
                        Klik event pada tanggal pelaksanaan untuk masuk ke laman
                        registrasi atau detail Live Teaching.
                      </small>
                      <Link to={all_routes.liveTeaching} className="btn btn-primary btn-sm">
                        Cari Live Teaching
                      </Link>
                    </div>
                  </section>
                  )}

                  {activeDashboardTab === 'dashboard' && (
                    <>
                  <h5 className="mb-3 fs-18">Akses Course Kamu</h5>
                  <div className="row">
                    {dashboardData?.course &&
                      dashboardData.course.length > 0 ? (
                      dashboardData.course.map((item: any, idx: number) => (
                        <div className="col-xl-4 col-md-6" key={item.id}>
                          <div className="course-item-two course-item mx-0">
                            <div className="course-img">
                              <Link
                                to={
                                  item?.id
                                    ? all_routes.studentCourseContent.replace(
                                      ':id',
                                      String(item.id),
                                    )
                                    : '#'
                                }
                              >
                                <ImageWithBasePath
                                  srcApi={item.image}
                                  src=""
                                  alt="img"
                                  className="img-fluid"
                                  fallbackSrc="/assets/img/course/course-48.jpg"
                                />
                              </Link>
                              <div className="position-absolute start-0 top-0 d-flex align-items-start w-100 z-index-2 p-3"></div>
                            </div>
                            <div className="course-content">
                              <div className="d-flex justify-content-between mb-2">
                                <div className="d-flex align-items-center">
                                  <span className="avatar avatar-sm">
                                    <ImageWithBasePath
                                      src="/assets/img/user/user-29.jpg"
                                      alt="img"
                                      className="img-fluid avatar avatar-sm rounded-circle"
                                    />
                                  </span>
                                  <div className="ms-2">
                                    <span className="link-default fs-14">
                                      {item.category?.name || '-'}
                                    </span>
                                  </div>
                                </div>
                                <span className="badge badge-light rounded-pill bg-light d-inline-flex align-items-center fs-13 fw-medium mb-0">
                                  {item.type}
                                </span>
                              </div>
                              <h6 className="title mb-2">
                                <Link
                                  to={
                                    item?.id
                                      ? all_routes.studentCourseContent.replace(
                                        ':id',
                                        String(item.id),
                                      )
                                      : '#'
                                  }
                                >
                                  {item.title}
                                </Link>
                              </h6>
                              <p className="d-flex align-items-center mb-3">
                                <i className="fa-solid fa-star text-warning me-2" />
                                {/* Rating not available in API, placeholder */}
                                -
                              </p>
                              <div className="d-flex align-items-center justify-content-between">
                                <h5 className="text-secondary mb-0">
                                  Rp
                                  {parseInt(item.price).toLocaleString('id-ID')}
                                </h5>
                                <Link
                                  to={
                                    item?.id
                                      ? all_routes.studentCourseContent.replace(
                                        ':id',
                                        String(item.id),
                                      )
                                      : '#'
                                  }
                                  className="btn btn-dark btn-sm d-inline-flex align-items-center"
                                >
                                  Lihat Detail
                                  <i className="isax isax-arrow-right-3 ms-1" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center text-muted">
                        Belum ada course
                      </div>
                    )}
                  </div>

                  {/* CBT History Section */}
                  <div className="row mt-4">
                    <div className="col-xl-12">
                      <div className="card mb-0">
                        <div className="card-body">
                          <h5 className="mb-3 fs-18 border-bottom pb-3">
                            Riwayat Ujian Kamu
                          </h5>
                          {dashboardData?.CBT &&
                            dashboardData.CBT.length > 0 ? (
                            dashboardData.CBT.map((cbt: any) => (
                              <div
                                key={cbt.id}
                                className="d-flex align-items-center flex-wrap flex-md-nowrap justify-content-between row-gap-2 mb-3"
                              >
                                <div>
                                  <h6 className="mb-1">{cbt.title}</h6>
                                  <div className="d-flex align-items-center">
                                    <p className="mb-0">
                                      Dibeli pada:{' '}
                                      {cbt.updated_at
                                        ? new Date(
                                          cbt.updated_at,
                                        ).toLocaleString('id-ID', {
                                          day: '2-digit',
                                          month: 'long',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit',
                                          hour12: false,
                                        })
                                        : '-'}
                                    </p>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  {cbt.is_answered && (
                                    <Link
                                      to={all_routes.studentCBTList.replace(':id', String(cbt.id))}
                                      className="btn btn-success btn-icon btn-sm"
                                      title="Lihat Review"
                                      aria-label="Lihat Review"
                                    >
                                      <i className="isax isax-eye" />
                                    </Link>
                                  )}
                                  {!cbt.is_finished && (
                                    <Link
                                      to={all_routes.studentCBTInstruction.replace(
                                        ':id',
                                        String(cbt.id))}
                                      className="btn btn-primary btn-icon btn-sm"
                                      title="Mulai CBT"
                                      aria-label="Mulai CBT"
                                    >
                                      <i className="isax isax-play" />
                                    </Link>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-muted">
                              Belum ada riwayat ujian
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default StudentDashboard
